/**
 * sl-wq59.6.4: Passkey (WebAuthn) sign-in button.
 *
 * Session-less usernameless resident-key flow — triggers the browser
 * authenticator UI (Face ID / Touch ID / Windows Hello / security key).
 * Hidden entirely when `navigator.credentials` is unavailable (older
 * browsers / non-HTTPS contexts).
 */
import { notify } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AuthService, isWebAuthnUserCancel } from '@affine/core/modules/cloud';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useMemo, useState } from 'react';

import * as style from './style.css';

type AuthI18n = ReturnType<typeof useI18n>;
type ErrorTranslationFn = (data?: unknown) => string;

/**
 * Passkey glyph — key icon aligned with the W3C/FIDO Alliance passkey
 * iconography guidance (passkeys.dev, passkeys.io). A simple key is the
 * recommended cross-platform symbol; OS-specific variants (Face ID, Touch ID,
 * Windows Hello) are selected by the authenticator UI itself, so the button
 * stays generic. Uses currentColor so it inherits the Button text color.
 */
const PasskeyIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <circle cx="9" cy="11" r="4" />
    <path d="M13 11h8" />
    <path d="M17 11v4" />
    <path d="M21 11v2" />
  </svg>
);

function getPasskeyErrorMessage(t: AuthI18n, error: UserFriendlyError): string {
  const key = `error.${error.name}` as keyof AuthI18n;
  const translate = t[key];
  if (typeof translate === 'function') {
    return (translate as ErrorTranslationFn)(error.data);
  }
  return t['com.streamlain.auth.toast.message.unexpected']();
}

export interface SignInWithPasskeyProps {
  /**
   * Callback invoked after the session has been revalidated. Parent can
   * trigger a redirect (mirrors the password/magic-link flow).
   */
  onAuthenticated?: () => void;
}

export const SignInWithPasskey = ({
  onAuthenticated,
}: SignInWithPasskeyProps) => {
  const t = useI18n();
  const authService = useService(AuthService);
  const [loading, setLoading] = useState(false);

  // Memoize the feature-detect so SSR / initial hydration doesn't flip the
  // button visibility.
  const supported = useMemo(() => AuthService.isWebAuthnSupported(), []);

  const onClick = useAsyncCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      await authService.signInPasskey();
      onAuthenticated?.();
    } catch (err) {
      // User-cancel gets a softer toast than a real failure.
      const isCancel = isWebAuthnUserCancel(err);
      const error = UserFriendlyError.fromAny(err);
      notify.error({
        title: isCancel
          ? t['com.streamlain.auth.sign.in.passkey.cancelled']()
          : t['com.streamlain.auth.sign.in.passkey.failed'](),
        message: isCancel ? undefined : getPasskeyErrorMessage(t, error),
      });
    } finally {
      setLoading(false);
    }
  }, [authService, loading, onAuthenticated, t]);

  if (!supported) {
    return null;
  }

  return (
    <div className={style.passkeySection}>
      <div className={style.passkeyDivider}>
        {t['com.streamlain.auth.sign.or-divider']()}
      </div>
      <Button
        className={style.passkeyButtonPill}
        data-testid="sign-in-with-passkey-button"
        variant="secondary"
        loading={loading}
        disabled={loading}
        prefix={<PasskeyIcon />}
        onClick={onClick}
      >
        {t['com.streamlain.auth.sign.in.passkey']()}
      </Button>
    </div>
  );
};
